/**
 * Bundled option lists for the static lookup filter pickers (FilterMultiCombobox).
 *
 * Values were verified against the production database on 2026-07-14 — each
 * display_string must match the prod row's `name` exactly (the backend indexes it
 * as the nested `<relation>.display_string` keyword the filter rule matches). If a
 * lookup row is ever added or renamed in prod, update the matching list here AND
 * in the tabasco seeder, then reindex the parent model.
 */

import { SCHEDULE_TYPE } from '@/utils/customPlaylistSchedule/constants';
import { SCHEDULE_KIND_LABELS } from '@/utils/customPlaylistSchedule/summary';

export interface LookupFilterOption {
  id: number;
  display_string: string;
}

export const ACCOUNT_STATUS_FILTER_OPTIONS = [
  { id: 1, display_string: 'Active' },
  { id: 2, display_string: 'Disabled' },
] as const satisfies readonly LookupFilterOption[];

export const LOCATION_STATUS_FILTER_OPTIONS = [
  { id: 1, display_string: 'Active' },
  { id: 2, display_string: 'Disabled' },
] as const satisfies readonly LookupFilterOption[];

export const REGION_FILTER_OPTIONS = [
  { id: 1, display_string: 'Unassigned' },
  { id: 2, display_string: 'NonUS' },
  { id: 3, display_string: 'Pacific - Nevada' },
  { id: 4, display_string: 'Pacific' },
  { id: 5, display_string: 'Mountain' },
  { id: 6, display_string: 'SouthEast' },
  { id: 7, display_string: 'NorthEast - NY' },
  { id: 8, display_string: 'NorthEast - DC' },
  { id: 9, display_string: 'NorthEast - PA' },
  { id: 10, display_string: 'NorthEast - All Else' },
  { id: 11, display_string: 'SouthCentral' },
  { id: 12, display_string: 'NorthCentral' },
  { id: 13, display_string: 'UK' },
  { id: 14, display_string: 'Canada' },
  { id: 15, display_string: 'Australia' },
  { id: 16, display_string: 'Mexico' },
  { id: 17, display_string: 'Brazil' },
  { id: 18, display_string: 'Europe' },
  { id: 19, display_string: 'Russia' },
  { id: 20, display_string: 'Pacific-CA' },
  { id: 21, display_string: 'Middle East' },
  { id: 22, display_string: 'Asia Pacific' },
  { id: 23, display_string: 'Greater China' },
  { id: 24, display_string: 'Japan' },
  { id: 25, display_string: 'India' },
  { id: 26, display_string: 'Latin America' },
  { id: 27, display_string: 'Taiwan' },
  { id: 28, display_string: 'Kazakhstan' },
] as const satisfies readonly LookupFilterOption[];

export const LYRICS_CHECK_STATUS_FILTER_OPTIONS = [
  { id: 1, display_string: 'Lyrics checked' },
  { id: 2, display_string: 'Lyrics checked - Foreign' },
  { id: 3, display_string: 'Lyrics checked - Instrumental' },
  { id: 4, display_string: 'Not checked' },
  { id: 5, display_string: 'Please Check' },
  { id: 6, display_string: 'Not Found' },
] as const satisfies readonly LookupFilterOption[];

export const STAGED_TRACK_SOURCE_FILTER_OPTIONS = [
  { id: 1, display_string: 'DDEX' },
  { id: 2, display_string: 'Manual' },
] as const satisfies readonly LookupFilterOption[];

export const PARENTAL_ADVISORY_TYPE_FILTER_OPTIONS = [
  { id: 1, display_string: 'No Rating' },
  { id: 2, display_string: 'Clean' },
  { id: 3, display_string: 'Explicit' },
] as const satisfies readonly LookupFilterOption[];

/**
 * The zone status that draws a zone's icon red (modelIcons), and names it in the
 * icon's tooltip. LocationZoneStatus::PendingCancelation on the backend.
 */
export const LOCATION_ZONE_STATUS_PENDING_CANCELATION = { id: 15, display_string: 'Pending Cancelation' } as const satisfies LookupFilterOption;

export const LOCATION_ZONE_STATUS_FILTER_OPTIONS = [
  { id: 1, display_string: 'Demo' },
  { id: 2, display_string: 'Monthly' },
  { id: 7, display_string: 'Internal' },
  { id: 10, display_string: 'Corporate DEMO' },
  { id: 12, display_string: 'Closed For Season' },
  { id: 13, display_string: 'Temporarily Closed' },
  LOCATION_ZONE_STATUS_PENDING_CANCELATION,
  { id: 17, display_string: 'Odd Months' },
  { id: 19, display_string: 'Pending New' },
  { id: 20, display_string: 'Even Months' },
  { id: 21, display_string: 'COVID Off' },
] as const satisfies readonly LookupFilterOption[];

export const CONTENT_STYLE_FILTER_OPTIONS = [
  { id: 1, display_string: 'Specialty' },
  { id: 2, display_string: 'Standard' },
  { id: 3, display_string: 'Style' },
] as const satisfies readonly LookupFilterOption[];

/** A custom list's status ids, the same as the backend's LocationZoneCustomPlaylistStatus constants. */
export const CUSTOM_LIST_STATUS = {
  SHOW_IN_SWITCHER: 1,
  DONT_SHOW_IN_SWITCHER: 2,
  /** Players skip a disabled list. */
  DISABLED: 3,
} as const;

export const LOCATION_ZONE_CUSTOM_PLAYLIST_STATUS_FILTER_OPTIONS = [
  { id: CUSTOM_LIST_STATUS.SHOW_IN_SWITCHER, display_string: 'Show in Station Switcher' },
  { id: CUSTOM_LIST_STATUS.DONT_SHOW_IN_SWITCHER, display_string: 'Don\'t Show in Station Switcher' },
  { id: CUSTOM_LIST_STATUS.DISABLED, display_string: 'Disabled' },
] as const satisfies readonly LookupFilterOption[];

export const LOCATION_ZONE_PLAYER_STREAM_MODE_FILTER_OPTIONS = [
  { id: 1, display_string: 'Offline' },
  { id: 2, display_string: 'Hybrid' },
  { id: 3, display_string: 'Stream' },
] as const satisfies readonly LookupFilterOption[];

export const CROSSFADE_TYPE_FILTER_OPTIONS = [
  { id: 1, display_string: 'Linear' },
  { id: 2, display_string: 'Logarithmic' },
  { id: 3, display_string: 'Exponential' },
  { id: 4, display_string: 'Equalpower' },
] as const satisfies readonly LookupFilterOption[];

export const PLAYER_TYPE_FILTER_OPTIONS = [
  { id: 1, display_string: 'physical' },
  { id: 2, display_string: 'virtual' },
  { id: 3, display_string: 'Mobile' },
  { id: 4, display_string: 'WSL' },
] as const satisfies readonly LookupFilterOption[];

export const PLAYER_SERVER_FILTER_OPTIONS = [
  { id: 3, display_string: '--don\'t use--' },
  { id: 4, display_string: 'remload.grayv.com' },
  { id: 6, display_string: 'remotefallback.grayv.com' },
  { id: 7, display_string: 'remotetest.grayv.com' },
  { id: 8, display_string: 'remload.theplaylistgeneration.com' },
] as const satisfies readonly LookupFilterOption[];

export const CONTACT_LOGIN_STATION_SOURCE_TYPE_FILTER_OPTIONS = [
  { id: 1, display_string: 'Zone' },
  { id: 3, display_string: 'Zone Custom Playlist' },
  { id: 4, display_string: 'Generated Playlist' },
  { id: 5, display_string: 'Contact Custom Playlist' },
  { id: 6, display_string: 'Sample Zone Custom List' },
] as const satisfies readonly LookupFilterOption[];

/**
 * Options for pickers on plain integer FK columns (no nested display_string is
 * indexed for these relations) — the rule sends ids, the label is display-only.
 */

export const LIVE_TRACK_ACTION_STATUS_FILTER_OPTIONS = [
  { id: 1, display_string: 'Unprocessed' },
  { id: 2, display_string: 'In process' },
  { id: 3, display_string: 'Success' },
  { id: 4, display_string: 'File update error' },
  { id: 5, display_string: 'Cover art update error' },
  { id: 6, display_string: 'Metadata update error' },
  { id: 7, display_string: 'Rollback failure' },
] as const satisfies readonly LookupFilterOption[];

export const RAW_DDEX_TRACK_TAKEDOWN_STATUS_FILTER_OPTIONS = [
  { id: 1, display_string: 'Takedown' },
  { id: 2, display_string: 'Deal Expired' },
  { id: 3, display_string: 'Territory Takedown' },
  { id: 4, display_string: 'Pre-Release Takedown' },
  { id: 5, display_string: 'Purge Release' },
] as const satisfies readonly LookupFilterOption[];

/** Mirror of the backend Brand constants (Brand::GRAY_V = 1, Brand::TPG = 2). */
export const PLAYER_BRAND_FILTER_OPTIONS = [
  { id: 1, display_string: 'Gray V' },
  { id: 2, display_string: 'TPG' },
] as const satisfies readonly LookupFilterOption[];

/** Slug -> options for pickers filtering nested `<relation>.display_string` keywords. */

export const DISPLAY_STRING_LOOKUP_OPTIONS: Record<string, readonly LookupFilterOption[]> = {

  account_status: ACCOUNT_STATUS_FILTER_OPTIONS,

  location_status: LOCATION_STATUS_FILTER_OPTIONS,

  region: REGION_FILTER_OPTIONS,

  lyrics_check_status: LYRICS_CHECK_STATUS_FILTER_OPTIONS,

  staged_track_source: STAGED_TRACK_SOURCE_FILTER_OPTIONS,

  parental_advisory_type: PARENTAL_ADVISORY_TYPE_FILTER_OPTIONS,

  location_zone_status: LOCATION_ZONE_STATUS_FILTER_OPTIONS,

  content_style: CONTENT_STYLE_FILTER_OPTIONS,

  location_zone_custom_playlist_status: LOCATION_ZONE_CUSTOM_PLAYLIST_STATUS_FILTER_OPTIONS,

  location_zone_player_stream_mode: LOCATION_ZONE_PLAYER_STREAM_MODE_FILTER_OPTIONS,

  crossfade_type: CROSSFADE_TYPE_FILTER_OPTIONS,

  player_type: PLAYER_TYPE_FILTER_OPTIONS,

  player_server: PLAYER_SERVER_FILTER_OPTIONS,

  contact_login_station_source_type: CONTACT_LOGIN_STATION_SOURCE_TYPE_FILTER_OPTIONS,

};


/** Slug -> options for pickers filtering integer FK columns by id. */

/**
 * Bucket types. Verified against the bucket_types table on 2026-09-15: ids 1-4 with
 * names Static, Dynamic, Mixed, Vault, matching BucketType::STATIC..VAULT in tabasco.
 *
 * Filter on `bucket_type_id`, the integer column. The search document ALSO carries a
 * `bucket_type` slug, but that is not a model field -- the backend deliberately does
 * not cast or append it, so the generator never emits it and no filter column exists
 * for it. See the note above filterControlMappings.
 *
 * Each row's name lowercased is the slug the search document carries, which is how
 * the dropdown resolves a bucket's mark without a second copy of those four strings.
 */
export const BUCKET_TYPE_FILTER_OPTIONS = [
  { id: 1, display_string: 'Static' },
  { id: 2, display_string: 'Dynamic' },
  { id: 3, display_string: 'Mixed' },
  { id: 4, display_string: 'Vault' },
] as const satisfies readonly LookupFilterOption[];

/**
 * Custom playlist schedule types. The ids and labels come from the schedule module,
 * so the filter and the schedule cards cannot drift apart.
 *
 * Silence is not a type of its own. It is a Message row with the silence flag set,
 * so this list has five entries and no Silence. The "Schedule" filter group offers
 * Silence as a yes/no field instead.
 */
export const CUSTOM_PLAYLIST_SCHEDULE_TYPE_FILTER_OPTIONS = [
  { id: SCHEDULE_TYPE.DATETIME, display_string: SCHEDULE_KIND_LABELS.oneoff },
  { id: SCHEDULE_TYPE.RECURRING, display_string: SCHEDULE_KIND_LABELS.recurring },
  { id: SCHEDULE_TYPE.STATION_CHANGE, display_string: SCHEDULE_KIND_LABELS.stationchange },
  { id: SCHEDULE_TYPE.INSERT, display_string: SCHEDULE_KIND_LABELS.insert },
  { id: SCHEDULE_TYPE.MESSAGING, display_string: SCHEDULE_KIND_LABELS.message },
] as const satisfies readonly LookupFilterOption[];

export const ID_LOOKUP_OPTIONS: Record<string, readonly LookupFilterOption[]> = {

  bucket_type: BUCKET_TYPE_FILTER_OPTIONS,

  live_track_action_status: LIVE_TRACK_ACTION_STATUS_FILTER_OPTIONS,

  raw_ddex_track_takedown_status: RAW_DDEX_TRACK_TAKEDOWN_STATUS_FILTER_OPTIONS,

  player_brand: PLAYER_BRAND_FILTER_OPTIONS,

  custom_playlist_schedule_type: CUSTOM_PLAYLIST_SCHEDULE_TYPE_FILTER_OPTIONS,

  // The lookup slug the bulk-action capabilities endpoint names for
  // `location_zone_custom_playlist_status_id`. The bulk edit dialog offers it as a single-value
  // select that sends the id. The list is the same one the relation column's name picker uses,
  // keyed here by the model's real API slug.
  custom_playlist_status: LOCATION_ZONE_CUSTOM_PLAYLIST_STATUS_FILTER_OPTIONS,

};


/** Every bundled lookup slug (used by useFilterOptions to resolve static options). */

export const STATIC_LOOKUP_OPTIONS: Record<string, readonly LookupFilterOption[]> = {

  ...DISPLAY_STRING_LOOKUP_OPTIONS,
  ...ID_LOOKUP_OPTIONS,
};

/**
 * Display string for a plain integer FK column backed by a bundled id lookup
 * (e.g. brand_id 2 → 'TPG'). Returns null when the options source isn't an id
 * lookup, the value isn't a number, or the id has no bundled option — callers
 * fall back to rendering the raw value.
 */
export function idLookupDisplayString(
  optionsSource: string | undefined,
  value: unknown,
): string | null {
  if (!optionsSource || typeof value !== 'number') {
    return null;
  }
  const options = ID_LOOKUP_OPTIONS[optionsSource];
  const matchingOption = options?.find((lookupOption) => lookupOption.id === value);
  return matchingOption?.display_string ?? null;
}
